import React from 'react';
import styles from './page.module.css';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import Titulo from '@/components/tipografia/Titulo';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoMemo from '@/components/Formulario/CampoMemo';
import Icon from '@/components/Icon';
import Tabs from '@/components/layout/Tabs';
import {
  mdiLoading,
  mdiMagnify,
  mdiRobot,
  mdiAccount,
  mdiHeadset,
  mdiCheckCircle,
  mdiBulletinBoard,
  mdiEyeCircleOutline,
  mdiMessageText,
  mdiHistory,
  mdiPlusCircleOutline,
  mdiFormatListBulleted,
} from '@mdi/js';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import FreeTierModal from '@/components/FreeTierModal';
import { socket } from '@/components/navegacao/socket';
import axios from 'axios';
import { useDebounce } from '../chat/useDebounce';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { ESTADOS_MENSAGEM_ENTREGUE } from '@tera/shared/src/dto/ChatMensagemDto';
import PromptDiffViewer, { SugestaoDiff, RegrasSnapshot } from './PromptDiffViewer';
import useWindowSize from '@/lib/useWindowSize';

type ChatItem = {
  id: number;
  nome: string;
  estado: string;
  canal_cliente_id?: string;
  total_mensagens: number;
  total_supervisoes: number;
  canal?: { id?: number; tipo?: string; nome?: string };
  data_ultima_atualizacao?: string;
  ultmsg_data_criacao?: string;
};

type MensagemItem = {
  id: number;
  origem: string;
  tipo_mime: string;
  conteudo_ou_url: string;
  metadados?: Record<string, any>;
  data_criacao: string;
};

type SupervisaoResultado = {
  o_que_aconteceu: string;
  regras_envolvidas: string[];
  // Formato novo (>= v2)
  sugestoes?: SugestaoDiff[];
  // Formato antigo (< v2) — apenas leitura, sem aplicação automática
  sugestoes_melhoria?: string[];
  prompt_revisado?: string;
};

type SupervisaoRecord = {
  id: number;
  chat_id: number;
  ia_agente_id?: number | null;
  ia_agente?: { id: number; nome: string };
  usuario?: { id: number; nome: string };
  resultado?: SupervisaoResultado;
  regras_snapshot?: RegrasSnapshot | null;
  data_criacao?: string;
};

// Renderização simplificada de mensagem para o preview da supervisão
const MensagemPreview = ({ msg }: { msg: MensagemItem }) => {
  const isIA = msg.origem === 'bot';
  const isCliente = msg.origem === 'usuario';

  const conteudo = msg.tipo_mime === 'text/message'
    ? msg.conteudo_ou_url
    : msg.tipo_mime?.startsWith('audio/') && msg.metadados?.transcricao
      ? `🎤 ${msg.metadados.transcricao}`
      : msg.tipo_mime?.startsWith('image/')
        ? '🖼️ (imagem)'
        : msg.tipo_mime?.startsWith('video/')
          ? '🎥 (vídeo)'
          : `📎 (${msg.tipo_mime})`;

  return (
    <div className={[styles.msgBubble, isIA ? styles.msgIA : isCliente ? styles.msgCliente : styles.msgAtendente].join(' ')}>
      <div className={styles.msgOrigem}>
        {isIA && <Icon path={mdiRobot} size={0.7} />}
        {isCliente && <Icon path={mdiAccount} size={0.7} />}
        {!isIA && !isCliente && <Icon path={mdiHeadset} size={0.7} />}
        <span>{isIA ? 'IA' : isCliente ? 'Cliente' : 'Atendente'}</span>
        <span className={styles.msgData}>
          {format(new Date(msg.data_criacao), 'HH:mm')}
        </span>
      </div>
      <div className={styles.msgConteudo}>{conteudo}</div>
    </div>
  );
};

const PROGRESS_MSGS = [
  'Analisando atendimento...',
  'Lendo as mensagens...',
  'Interpretando o contexto...',
  'Identificando padrões...',
  'Elaborando sugestões...',
  'Quase lá...',
  'Pensando...',
  'Processando as informações...',
  'Montando uma resposta...'
];

// Tipo estendido retornado pelo job completo (inclui regras_snapshot)
type ChatSupervisaoResultadoFull = {
  id?: number;
  ia_agente_id?: number | null;
  resultado?: SupervisaoResultado;
  regras_snapshot?: RegrasSnapshot | null;
};

/** Calcula SHA-256 de uma string via SubtleCrypto (browser-native) */
async function sha256(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const Page = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [showFreeTierModal, setShowFreeTierModal] = React.useState(false);

  // Buscar capacidades do plano
  const capacidadesQuery = useQuery(
    ['capacidades'],
    async () => {
      const res = await axios.get('/api/capacidades', { withCredentials: true });
      return res.data;
    },
    { refetchOnWindowFocus: false, staleTime: 30000 }
  );

  // No mobile o LayoutUsuario usa min-height no container pai, causando scroll externo.
  // Corrigimos diretamente nos elementos pai/avô ao montar a página.
  // A sidebar (position:absolute) usa o #root como containing block (container é static),
  // então overflow:hidden no #root é necessário para impedir o scroll externo no mobile.
  const [, , isMobileSize] = useWindowSize(1100);
  const pageLayoutRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const parent = pageLayoutRef.current?.parentElement as HTMLElement | null;
    const grandParent = parent?.parentElement as HTMLElement | null;
    const rootEl = document.getElementById('root') as HTMLElement | null;
    if (!parent) return;
    const prevOverflow = parent.style.overflow;
    const prevMinHeight = parent.style.minHeight;
    const prevHeight = parent.style.height;
    parent.style.overflow = 'hidden';
    parent.style.minHeight = '0';
    parent.style.height = '100%';
    const prevGpOverflow = grandParent?.style.overflow ?? '';
    if (grandParent) grandParent.style.overflow = 'hidden';
    const prevRootOverflow = rootEl?.style.overflow ?? '';
    if (rootEl) rootEl.style.overflow = 'hidden';
    return () => {
      parent.style.overflow = prevOverflow;
      parent.style.minHeight = prevMinHeight;
      parent.style.height = prevHeight;
      if (grandParent) grandParent.style.overflow = prevGpOverflow;
      if (rootEl) rootEl.style.overflow = prevRootOverflow;
    };
  }, []);

  const selectedChatId = searchParams.get('chat_id') ? +searchParams.get('chat_id')! : null;
  const filterCanalId = searchParams.get('canal_id') ?? undefined;
  const filterCanalClienteId = searchParams.get('canal_cliente_id') ?? undefined;

  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebounce(search, 500);

  const [contexto, setContexto] = React.useState('');
  const [oQueHouve, setOQueHouve] = React.useState('');
  const [comportamentoEsperado, setComportamentoEsperado] = React.useState('');
  const [resultadoAtual, setResultadoAtual] = React.useState<SupervisaoResultado | null>(null);
  const [resultadoMeta, setResultadoMeta] = React.useState<{ data?: string; usuario?: string } | null>(null);
  const [historicoSelecionadoId, setHistoricoSelecionadoId] = React.useState<number | null>(null);
  const [regrasSnapshotAtual, setRegrasSnapshotAtual] = React.useState<RegrasSnapshot | null>(null);
  const [selectedSugestoes, setSelectedSugestoes] = React.useState<Set<number>>(new Set());
  const [pendingJobId, setPendingJobId] = React.useState<string | null>(null);
  const [progressMsg, setProgressMsg] = React.useState(PROGRESS_MSGS[0]);
  const [activeTab, setActiveTab] = React.useState<'nova-analise' | 'resultado' | 'historico'>('nova-analise');

  // Reset form when chat changes
  React.useEffect(() => {
    setContexto('');
    setOQueHouve('');
    setComportamentoEsperado('');
    setResultadoAtual(null);
    setResultadoMeta(null);
    setHistoricoSelecionadoId(null);
    setPendingJobId(null);
    setRegrasSnapshotAtual(null);
    setSelectedSugestoes(new Set());
    setActiveTab('nova-analise');
  }, [selectedChatId]);

  // Rotação de mensagens de progresso
  React.useEffect(() => {
    if (!pendingJobId) return;
    let idx = 0;
    setProgressMsg(PROGRESS_MSGS[0]);
    const interval = setInterval(() => {
      idx = (idx + 1) % PROGRESS_MSGS.length;
      setProgressMsg(PROGRESS_MSGS[idx]);
    }, 3000);
    return () => clearInterval(interval);
  }, [pendingJobId]);

  // Polling do job de supervisão
  const queryJobStatus = useQuery(
    ['supervisao_job', pendingJobId],
    async () => {
      const res = await axios.get(`/api/chat_supervisao/job/${pendingJobId}`, { withCredentials: true });
      return res.data;
    },
    {
      enabled: !!pendingJobId,
      refetchInterval: (data: any) =>
        !data || data.state === 'waiting' || data.state === 'active' ? 2000 : false,
      onSuccess: (data: any) => {
        if (data.state === 'completed') {
          const s: ChatSupervisaoResultadoFull = data.result;
          setResultadoAtual(s?.resultado ?? null);
          setRegrasSnapshotAtual(s?.regras_snapshot ?? null);
          setSelectedSugestoes(new Set(s?.resultado?.sugestoes?.map((_: any, i: number) => i) ?? []));
          setResultadoMeta(null);
          setHistoricoSelecionadoId(null);
          setPendingJobId(null);
          setActiveTab('resultado');
          queryHistorico.refetch();
          queryClient.invalidateQueries(['ia_supervisao_chats']);
        } else if (data.state === 'failed') {
          toast(data.failedReason ?? 'Erro ao analisar conversa. Tente novamente.', {
            position: 'top-center',
            type: 'error',
            autoClose: 5000,
          });
          setPendingJobId(null);
        }
      },
      onError: () => {
        toast('Erro ao verificar o status da análise.', {
          position: 'top-center',
          type: 'error',
          autoClose: 5000,
        });
        setPendingJobId(null);
      },
    }
  );

  // Lista de chats com IA
  const queryChats = useInfiniteQuery({
    queryKey: ['ia_supervisao_chats', debouncedSearch, filterCanalId, filterCanalClienteId],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ t: '30' });
      if (debouncedSearch) params.set('txt', debouncedSearch);
      if (pageParam) params.set('c', pageParam);
      if (filterCanalId) params.set('canal_id', filterCanalId);
      if (filterCanalClienteId) params.set('canal_cliente_id', filterCanalClienteId);
      const res = await axios.get(`/api/chat_supervisao/chats?${params.toString()}`, {
        withCredentials: true,
      });
      return res.data;
    },
    getNextPageParam: (lastPage: any) => lastPage?.cursor ?? null,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const chats: ChatItem[] = queryChats.data?.pages?.flatMap((p: any) => p.result ?? []) ?? [];

  // Mensagens do chat selecionado
  const queryMensagens = useQuery(
    ['ia_supervisao_mensagens', selectedChatId],
    async () => {
      if (!selectedChatId) return { result: [] };
      const params = new URLSearchParams({
        t: '200',
        so: 'asc',
        fi: JSON.stringify({
          'chat_mensagem.chat_id': ['eq', selectedChatId],
          'chat_mensagem.origem': ['in', ['usuario', 'bot', 'atendente']],
          // Só mensagens entregues (o endpoint não restringe estado por padrão).
          'chat_mensagem.estado_atual': ['in', ESTADOS_MENSAGEM_ENTREGUE],
        }),
      });
      const res = await axios.get(`/api/chat_mensagem?${params.toString()}`, {
        withCredentials: true,
      });
      return res.data;
    },
    {
      enabled: !!selectedChatId,
      refetchOnWindowFocus: false,
      staleTime: 60000,
    }
  );

  const mensagens: MensagemItem[] = queryMensagens.data?.result ?? [];

  // Histórico de supervisões do chat selecionado
  const queryHistorico = useQuery(
    ['chat_supervisao_historico', selectedChatId],
    async () => {
      if (!selectedChatId) return { result: [] };
      const res = await axios.get(`/api/chat_supervisao?chat_id=${selectedChatId}`, {
        withCredentials: true,
      });
      return res.data;
    },
    {
      enabled: !!selectedChatId,
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  );

  const historico: SupervisaoRecord[] = queryHistorico.data?.result ?? [];

  const analisarMutation = useMutation(
    async () => {
      if (!selectedChatId) throw new Error('Nenhum chat selecionado');
      const res = await axios.post(
        '/api/chat_supervisao',
        {
          chat_id: selectedChatId,
          contexto: contexto || undefined,
          o_que_houve: oQueHouve || undefined,
          comportamento_esperado: comportamentoEsperado || undefined,
        },
        { withCredentials: true }
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        if (data.jobId) {
          setResultadoAtual(null);
          setResultadoMeta(null);
          setHistoricoSelecionadoId(null);
          setPendingJobId(data.jobId);
        } else {
          // fallback caso o backend retorne resultado direto
          setResultadoAtual(data.result?.resultado ?? null);
          setResultadoMeta(null);
          setHistoricoSelecionadoId(null);
          setActiveTab('resultado');
          queryHistorico.refetch();
        }
      },
      onError: (err: any) => {
        toast(err.response?.data?.msg ?? 'Erro ao analisar conversa', {
          position: 'top-center',
          type: 'warning',
          autoClose: 5000,
        });
      },
    }
  );

  const isAnalyzing = analisarMutation.isLoading || !!pendingJobId;

  // Busca o agente atual para comparar o hash de regras
  const agenteAtualId = regrasSnapshotAtual
    ? (historico.find((h) => h.regras_snapshot?.hash === regrasSnapshotAtual?.hash)?.ia_agente_id
      ?? historico.find((h) => h.id === historicoSelecionadoId)?.ia_agente_id
      ?? null)
    : null;

  const queryAgenteAtual = useQuery(
    ['ia_agente', agenteAtualId],
    async () => {
      const res = await axios.get(`/api/ia_agente/${agenteAtualId}`, { withCredentials: true });
      return res.data?.result ?? null;
    },
    { enabled: !!agenteAtualId, staleTime: 30000, refetchOnWindowFocus: false }
  );

  const [hashAtual, setHashAtual] = React.useState<string | null>(null);
  React.useEffect(() => {
    const agente = queryAgenteAtual.data;
    if (!agente) { setHashAtual(null); return; }
    const input = `${agente.pode_fazer?.trim() ?? ''}\n---\n${agente.nao_pode_fazer?.trim() ?? ''}`;
    sha256(input).then(setHashAtual);
  }, [queryAgenteAtual.data]);

  // Mutation: aplica sugestões selecionadas no agente
  const aplicarMutation = useMutation(
    async () => {
      if (!agenteAtualId || !regrasSnapshotAtual || !resultadoAtual?.sugestoes) throw new Error('Dados insuficientes');
      const agente = queryAgenteAtual.data;
      if (!agente) throw new Error('Agente não carregado');

      let podeFazer: string = agente.pode_fazer ?? '';
      let naoPodeFazer: string = agente.nao_pode_fazer ?? '';

      const selecionadas = resultadoAtual.sugestoes.filter((_, i) => selectedSugestoes.has(i));
      for (const s of selecionadas) {
        if (s.campo === 'pode_fazer') {
          podeFazer = s.texto_original
            ? podeFazer.replace(s.texto_original, s.texto_novo)
            : podeFazer + (s.texto_novo ? '\n' + s.texto_novo : '');
        } else {
          naoPodeFazer = s.texto_original
            ? naoPodeFazer.replace(s.texto_original, s.texto_novo)
            : naoPodeFazer + (s.texto_novo ? '\n' + s.texto_novo : '');
        }
      }

      await axios.put(`/api/ia_agente/${agenteAtualId}`, { pode_fazer: podeFazer, nao_pode_fazer: naoPodeFazer }, { withCredentials: true });
      return { podeFazer, naoPodeFazer };
    },
    {
      onSuccess: ({ podeFazer, naoPodeFazer }) => {
        toast('Regras do agente atualizadas com sucesso!', { type: 'success', position: 'top-center', autoClose: 4000 });
        queryClient.invalidateQueries(['ia_agente', agenteAtualId]);
        // Recalcular hash local para refletir o novo estado
        const input = `${podeFazer.trim()}\n---\n${naoPodeFazer.trim()}`;
        sha256(input).then(setHashAtual);
      },
      onError: (err: any) => {
        toast(err.response?.data?.msg ?? 'Erro ao aplicar alterações.', { type: 'error', position: 'top-center', autoClose: 5000 });
      },
    }
  );

  const selectedChat = chats.find((c) => c.id === selectedChatId);

  const estadoLabel: Record<string, string> = {
    chatbot: 'Chatbot',
    finalizado: 'Finalizado',
  };

  // Painel: seleção no chat mobile vai para nova-analise
  const handleSelectChat = (chatId: number) => {
    const next: Record<string, string> = { chat_id: String(chatId) };
    if (filterCanalId) next.canal_id = filterCanalId;
    if (filterCanalClienteId) next.canal_cliente_id = filterCanalClienteId;
    setSearchParams(next);
    // setActiveTab via useEffect em selectedChatId
  };

  // Conteúdo do painel esquerdo (lista de conversas)
  const chatListContent = (
    <>
      {queryChats.isFetching && !queryChats.data && (
        <HBox style={{ padding: '24px', justifyContent: 'center' }}>
          <Icon path={mdiLoading} size={1} spin />
        </HBox>
      )}
      {chats.map((chat) => (
        <div
          key={chat.id}
          className={[
            styles.chatItem,
            chat.id === selectedChatId ? styles.chatItemSelected : '',
          ].join(' ')}
          onClick={() => handleSelectChat(chat.id)}
        >
          <div className={styles.chatItemNome}>{chat.nome}</div>
          <div className={styles.chatItemInfo}>
            #{chat.id} · {estadoLabel[chat.estado] ?? chat.estado} · {chat.total_mensagens} mensagens
            {chat.ultmsg_data_criacao
              ? ` · ${format(new Date(chat.ultmsg_data_criacao), 'dd/MM HH:mm')}`
              : ''}
          </div>
          {chat.total_supervisoes > 0 && (
            <div className={styles.chatItemSupervisoes}>
              <Icon path={mdiEyeCircleOutline} size={0.55} />
              {chat.total_supervisoes} {chat.total_supervisoes === 1 ? 'supervisão' : 'supervisões'}
            </div>
          )}
        </div>
      ))}
      {queryChats.hasNextPage && (
        <div style={{ padding: '12px', textAlign: 'center' }}>
          <Botao
            variant="outline-secondary"
            onClick={() => queryChats.fetchNextPage()}
            disabled={queryChats.isFetchingNextPage}
          >
            {queryChats.isFetchingNextPage ? (
              <Icon path={mdiLoading} size={0.8} spin />
            ) : (
              'Carregar mais'
            )}
          </Botao>
        </div>
      )}
      {!queryChats.isFetching && chats.length === 0 && (
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--tc-color-gray-400)', fontSize: 13 }}>
          Nenhuma conversa com IA encontrada
        </div>
      )}
    </>
  );

  // Conteúdo da aba Nova análise
  const tabNovaAnalise = (
    <div className={styles.tabContent}>
      {!selectedChatId ? (
        <div className={styles.tabEmpty}>
          <Icon path={mdiEyeCircleOutline} size={2} color="var(--tc-color-gray-300)" />
          <span>Selecione uma conversa para supervisionar</span>
        </div>
      ) : (
        <VBox gap="20px">
          {selectedChat && (
            <div className={styles.sectionCard}>
              <HBox gap="8px" style={{ alignItems: 'center' }}>
                <Icon path={mdiRobot} size={1} color="var(--tc-color-primary)" />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{selectedChat.nome}</div>
                  <div style={{ fontSize: 12, color: 'var(--tc-color-gray-500)' }}>
                    #{selectedChat.id} · {estadoLabel[selectedChat.estado] ?? selectedChat.estado}
                    {selectedChat.canal?.nome ? ` · ${selectedChat.canal.nome}` : ''}
                  </div>
                </div>
              </HBox>
            </div>
          )}

          {/* Preview das mensagens */}
          <div className={styles.sectionCard}>
            <div className={styles.sectionTitle}>
              <HBox gap="6px">
                <Icon path={mdiMessageText} size={0.8} color="var(--tc-color-gray-500)" />
                Conversa
                {mensagens.length > 0 && (
                  <span style={{ fontWeight: 400, color: 'var(--tc-color-gray-400)' }}>
                    ({mensagens.length} mensagens)
                  </span>
                )}
              </HBox>
            </div>
            {queryMensagens.isFetching && (
              <HBox style={{ justifyContent: 'center', padding: '16px' }}>
                <Icon path={mdiLoading} size={1} spin color="var(--tc-color-gray-400)" />
              </HBox>
            )}
            {!queryMensagens.isFetching && mensagens.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--tc-color-gray-400)', textAlign: 'center', padding: '12px' }}>
                Sem mensagens visíveis
              </div>
            )}
            {mensagens.length > 0 && (
              <div className={styles.msgList}>
                {mensagens.map((msg) => (
                  <MensagemPreview key={msg.id} msg={msg} />
                ))}
              </div>
            )}
          </div>

          {/* Formulário de contexto */}
          <div className={styles.sectionCard}>
            <div className={styles.sectionTitle}>Contexto para análise (opcional)</div>
            <VBox gap="12px">
              <CampoMemo
                label="O que houve de errado?"
                placeholder="Descreva o problema observado no atendimento..."
                value={oQueHouve}
                onChange={setOQueHouve}
                rows={3}
              />
              <CampoMemo
                label="Qual era o comportamento esperado?"
                placeholder="Descreva como a IA deveria ter se comportado..."
                value={comportamentoEsperado}
                onChange={setComportamentoEsperado}
                rows={3}
              />
              <CampoMemo
                label="Contexto adicional"
                placeholder="Informações adicionais que podem ajudar na análise..."
                value={contexto}
                onChange={setContexto}
                rows={2}
              />
              {pendingJobId ? (
                <div className={styles.progressContainer}>
                  <div className={styles.progressBar}>
                    <div className={styles.progressBarFill} />
                  </div>
                  <div className={styles.progressMsg}>
                    <Icon path={mdiLoading} size={0.75} spin />
                    {progressMsg}
                  </div>
                </div>
              ) : (
                <Botao
                  variant="primary"
                  onClick={() => {
                    if (capacidadesQuery.data?.freeTier) {
                      setShowFreeTierModal(true);
                      return;
                    }
                    analisarMutation.mutate();
                  }}
                  disabled={isAnalyzing}
                >
                  {analisarMutation.isLoading ? (
                    <HBox gap="8px">
                      <Icon path={mdiLoading} size={0.8} spin />
                      Enviando...
                    </HBox>
                  ) : (
                    <HBox gap="8px">
                      <Icon path={mdiEyeCircleOutline} size={0.8} />
                      Analisar atendimento
                    </HBox>
                  )}
                </Botao>
              )}
            </VBox>
          </div>
        </VBox>
      )}
    </div>
  );

  // Conteúdo da aba Resultado
  const tabResultado = (
    <div className={styles.tabContent}>
      {!resultadoAtual ? (
        <div className={styles.tabEmpty}>
          <Icon path={mdiCheckCircle} size={2} color="var(--tc-color-gray-300)" />
          <span>Nenhum resultado ainda. Execute uma análise ou selecione um item do histórico.</span>
        </div>
      ) : (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>
            <HBox gap="6px" style={{ alignItems: 'center' }}>
              <Icon path={mdiCheckCircle} size={0.8} color="var(--tc-color-success)" />
              Resultado da análise
              {resultadoMeta?.data && (
                <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--tc-color-gray-400)', textTransform: 'none', letterSpacing: 0 }}>
                  · {format(new Date(resultadoMeta.data), "dd/MM/yyyy 'às' HH:mm")}
                  {resultadoMeta.usuario ? ` · por ${resultadoMeta.usuario}` : ''}
                </span>
              )}
            </HBox>
          </div>
          <VBox gap="16px">
            {resultadoAtual.o_que_aconteceu && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--tc-color-gray-600)' }}>
                  O que aconteceu
                </div>
                <div className={styles.resultItem}>{resultadoAtual.o_que_aconteceu}</div>
              </div>
            )}
            {resultadoAtual.regras_envolvidas?.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--tc-color-gray-600)' }}>
                  Regras envolvidas
                </div>
                <VBox gap="6px">
                  {resultadoAtual.regras_envolvidas.map((regra, i) => (
                    <div key={i} className={[styles.resultItem, styles.resultItemWarning].join(' ')}>
                      {regra}
                    </div>
                  ))}
                </VBox>
              </div>
            )}
            {resultadoAtual.sugestoes != null && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: 'var(--tc-color-gray-600)' }}>
                  Sugestões de alteração
                </div>
                <PromptDiffViewer
                  sugestoes={resultadoAtual.sugestoes}
                  regrasSnapshot={regrasSnapshotAtual}
                  hashAtual={hashAtual}
                  loadingHashAtual={queryAgenteAtual.isFetching}
                  selected={selectedSugestoes}
                  onToggle={(i) => {
                    aplicarMutation.reset();
                    setSelectedSugestoes((prev) => {
                      const next = new Set(prev);
                      if (next.has(i)) next.delete(i); else next.add(i);
                      return next;
                    });
                  }}
                  onSelectAll={() => {
                    aplicarMutation.reset();
                    setSelectedSugestoes(new Set(resultadoAtual.sugestoes?.map((_, i) => i) ?? []));
                  }}
                  onDeselectAll={() => {
                    aplicarMutation.reset();
                    setSelectedSugestoes(new Set());
                  }}
                  onAplicar={() => aplicarMutation.mutate()}
                  applyLoading={aplicarMutation.isLoading}
                  applyDone={aplicarMutation.isSuccess}
                />
              </div>
            )}
            {resultadoAtual.sugestoes == null && resultadoAtual.sugestoes_melhoria && resultadoAtual.sugestoes_melhoria.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--tc-color-gray-600)' }}>
                  Sugestões de melhoria
                  <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--tc-color-gray-400)', marginLeft: 6 }}>(formato legado — aplicação manual necessária)</span>
                </div>
                <VBox gap="6px">
                  {resultadoAtual.sugestoes_melhoria.map((sug, i) => (
                    <div key={i} className={[styles.resultItem, styles.resultItemSuccess].join(' ')}>{sug}</div>
                  ))}
                </VBox>
                {resultadoAtual.prompt_revisado && (
                  <pre className={styles.promptRevisado} style={{ marginTop: 10 }}>{resultadoAtual.prompt_revisado}</pre>
                )}
              </div>
            )}
          </VBox>
        </div>
      )}
    </div>
  );

  // Conteúdo da aba Histórico
  const tabHistorico = (
    <div className={styles.tabContent}>
      {historico.length === 0 ? (
        <div className={styles.tabEmpty}>
          <Icon path={mdiHistory} size={2} color="var(--tc-color-gray-300)" />
          <span>{selectedChatId ? 'Nenhuma supervisão registrada para esta conversa.' : 'Selecione uma conversa para ver o histórico.'}</span>
        </div>
      ) : (
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>
            <HBox gap="6px">
              <Icon path={mdiBulletinBoard} size={0.8} color="var(--tc-color-gray-500)" />
              Histórico de supervisões ({historico.length})
            </HBox>
          </div>
          <VBox gap="10px">
            {historico.map((h) => (
              <div
                key={h.id}
                className={[
                  styles.historySupervisao,
                  historicoSelecionadoId === h.id ? styles.historySupervisaoSelecionado : '',
                ].join(' ')}
                onClick={() => {
                  setResultadoAtual(h.resultado ?? null);
                  setRegrasSnapshotAtual(h.regras_snapshot ?? null);
                  setSelectedSugestoes(new Set(h.resultado?.sugestoes?.map((_, i) => i) ?? []));
                  setResultadoMeta({ data: h.data_criacao, usuario: h.usuario?.nome });
                  setHistoricoSelecionadoId(h.id);
                  setActiveTab('resultado');
                }}
              >
                <div className={styles.historyDate}>
                  {h.data_criacao
                    ? format(new Date(h.data_criacao), "dd/MM/yyyy 'às' HH:mm")
                    : ''}
                  {h.usuario?.nome ? ` · por ${h.usuario.nome}` : ''}
                  {h.ia_agente?.nome ? ` · Agente: ${h.ia_agente.nome}` : ''}
                </div>
                {h.resultado?.o_que_aconteceu && (
                  <div style={{ fontSize: 13, color: 'var(--tc-color-gray-700)' }}>
                    {h.resultado.o_que_aconteceu}
                  </div>
                )}
              </div>
            ))}
          </VBox>
        </div>
      )}
    </div>
  );

  const rightTabs = [
    { id: 'nova-analise', label: 'Nova análise', icon: mdiPlusCircleOutline, content: tabNovaAnalise },
    { id: 'resultado', label: 'Resultado', icon: mdiCheckCircle, content: tabResultado },
    { id: 'historico', label: 'Histórico', icon: mdiHistory, content: tabHistorico },
  ];

  const mobileTabs = [
    { id: 'conversas', label: 'Conversas', icon: mdiFormatListBulleted, content: <div className={styles.tabContent}><div className={styles.chatList}>{chatListContent}</div></div> },
    ...rightTabs,
  ];

  return (
    <LayoutUsuario>
      <FreeTierModal
        show={showFreeTierModal}
        onClose={() => setShowFreeTierModal(false)}
        feature="supervisão de IA"
      />
      <div ref={pageLayoutRef} className={styles.pageLayout}>
        {/* Painel esquerdo — visível apenas desktop */}
        <div className={styles.leftPanel}>
          <div className={styles.leftPanelHeader}>
            <Titulo>
              <HBox gap="8px">
                <Icon path={mdiEyeCircleOutline} size={1} />
                Supervisão de IA
              </HBox>
            </Titulo>
            <div style={{ marginTop: '12px' }}>
              <CampoTexto
                placeholder="Buscar conversa..."
                value={search}
                onChange={setSearch}
                prepend={<Icon path={mdiMagnify} size={0.8} />}
              />
            </div>
          </div>
          <div className={styles.chatList}>
            {chatListContent}
          </div>
        </div>

        {/* Painel direito com abas — desktop */}
        <div className={styles.rightPanel}>
          {!selectedChatId ? (
            <div className={styles.rightPanelEmpty}>
              <VBox center vcenter gap="8px">
                <Icon path={mdiEyeCircleOutline} size={2} color="var(--tc-color-gray-300)" />
                <span>Selecione uma conversa para supervisionar</span>
              </VBox>
            </div>
          ) : (
            <Tabs
              tabs={rightTabs}
              selectedTab={activeTab}
              onTabChange={(id) => setActiveTab(id as typeof activeTab)}
              size={isMobileSize ? 'sm' : 'lg'}
              className={styles.rightTabs}
            />
          )}
        </div>

        {/* Layout mobile — abas cobrindo tudo */}
        <div className={styles.mobileTabs}>
          <Tabs
            tabs={mobileTabs}
            selectedTab={activeTab === 'nova-analise' && !selectedChatId ? 'conversas' : activeTab}
            onTabChange={(id) => {
              if (id === 'conversas') setActiveTab('nova-analise');
              else setActiveTab(id as typeof activeTab);
            }}
            size="sm"
            className={styles.mobileTabsInner}
          />
        </div>
      </div>
    </LayoutUsuario>
  );
};

export default Page;
